import React from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    CircularProgress,
    alpha,
} from '@mui/material';
import { ExpandMore, Refresh, SportsBaseball } from '@mui/icons-material';
import useTheme from '@mui/system/useTheme';
import { BaseballRecruitProfile } from 'api/BaseballRecruits';

export interface GetPlayerStatsSectionProps {
    scrapingAll: boolean;
    scrapingProfiles: Set<string>;
    scrapingTotal: number;
    profiles: BaseballRecruitProfile[];
    searchingMembers: boolean;
    searchingBr: boolean;
    isActive: boolean;
    onScrapeAll: () => void;
    onSearchMembers: () => void;
    onSearchBr: () => void;
}

const GetPlayerStatsSection: React.FC<GetPlayerStatsSectionProps> = ({
    scrapingAll,
    scrapingProfiles,
    scrapingTotal,
    profiles,
    searchingMembers,
    searchingBr,
    isActive,
    onScrapeAll,
    onSearchMembers,
    onSearchBr,
}) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 2,
                p: 1.5,
                background: alpha(theme.palette.primary.main, 0.02),
                order: { xs: 1, md: 2 },
            }}
        >
            <Typography
                variant="overline"
                sx={{
                    color: 'primary.main',
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: 1,
                    letterSpacing: 1,
                    fontSize: '0.85rem',
                }}
            >
                Get Player Stats
            </Typography>
            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                    mb: 1,
                }}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img
                            src="/maxpreps-logo.png"
                            alt="MaxPreps"
                            style={{ height: 20, objectFit: 'contain' }}
                        />
                        <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                            Player Stats
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={onScrapeAll}
                            disabled={
                                scrapingAll ||
                                scrapingProfiles.size > 0 ||
                                profiles.filter((p) => p.maxpreps_url).length === 0
                            }
                            startIcon={
                                scrapingAll || scrapingProfiles.size > 0 ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <Refresh />
                                )
                            }
                        >
                            {scrapingProfiles.size > 0
                                ? `Refreshing ${
                                      scrapingTotal - scrapingProfiles.size
                                  } / ${scrapingTotal}`
                                : `Refresh Stats (${
                                      profiles.filter((p) => p.maxpreps_url).length
                                  })`}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onSearchMembers}
                            disabled={searchingMembers || isActive}
                            startIcon={
                                searchingMembers ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <SportsBaseball />
                                )
                            }
                        >
                            {searchingMembers ? 'Searching...' : 'Search'}
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>
            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SportsBaseball sx={{ color: 'primary.main' }} fontSize="small" />
                        <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                            Baseball Reference Player Stats
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onSearchBr}
                            disabled={searchingBr || isActive}
                            startIcon={
                                searchingBr ? <CircularProgress size={16} /> : <SportsBaseball />
                            }
                        >
                            {searchingBr ? 'Searching...' : 'Search'}
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default GetPlayerStatsSection;
